import type { ReactNode } from 'react';
import { DropdownMenu } from 'radix-ui';
import { cn } from '@/lib/cn';

export function Menu({ trigger, children, align = 'end', label }: { trigger: ReactNode; children: ReactNode; align?: 'start' | 'end'; label?: string }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          aria-label={label}
          className="z-50 min-w-48 rounded-[var(--radius-panel)] border border-line bg-surface p-1 shadow-[var(--shadow-pop)]"
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuItem({ children, onSelect, icon, tone, disabled }: { children: ReactNode; onSelect?: () => void; icon?: ReactNode; tone?: 'danger'; disabled?: boolean }) {
  return (
    <DropdownMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-surface-3 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        tone === 'danger' ? 'text-suspicious' : 'text-ink',
      )}
    >
      {icon && <span className="text-ink-3 [&>svg]:size-4">{icon}</span>}
      {children}
    </DropdownMenu.Item>
  );
}

export function MenuRadio({ value, onValueChange, options }: { value: string; onValueChange: (v: string) => void; options: { value: string; label: ReactNode }[] }) {
  return (
    <DropdownMenu.RadioGroup value={value} onValueChange={onValueChange}>
      {options.map((o) => (
        <DropdownMenu.RadioItem
          key={o.value}
          value={o.value}
          className="relative flex cursor-pointer select-none items-center gap-2 rounded-[var(--radius-ctl)] py-1.5 pl-7 pr-2.5 text-sm outline-none data-[highlighted]:bg-surface-3"
        >
          <DropdownMenu.ItemIndicator className="absolute left-2.5 inline-block size-2 rounded-full bg-accent" />
          {o.label}
        </DropdownMenu.RadioItem>
      ))}
    </DropdownMenu.RadioGroup>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className="px-2.5 pb-1 pt-2 text-xs font-semibold text-ink-3">{children}</DropdownMenu.Label>;
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-line" />;
}
